import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
    Alert,
    Anchor,
    Badge,
    Box,
    Button,
    Card,
    Center,
    Checkbox,
    Divider,
    Group,
    List,
    Loader,
    Modal,
    Pagination,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconRocket, IconSearch, IconTrash } from '@tabler/icons-react';
import type { CandidateEntry, CandidateSortField, SortOrder } from '../api/types';
import {
    bulkDeleteCandidates,
    listCandidateProviders,
    listCandidates,
    promoteCandidates,
    type BulkDeleteCandidatesResult,
    type PromoteCandidatesResult,
} from '../api/candidates';
import { SortableTh } from '../components/SortableTh';
import { NoTenantsFilter } from '../components/NoTenantsFilter';
import { OsintReportFilter } from '../components/OsintReportFilter';
import { ProviderFilter } from '../components/ProviderFilter';
import { safeHttpUrl } from '../utils/url';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = ['25', '50', '100', '250'];

/**
 * Candidate triage view — lists rows from `catalog_product_candidate` (the
 * pre-curation staging layer fed by the Snowflake ingest, the Safebase
 * webhook, and seed migrations). Rendered at `/candidates`; a row click
 * navigates to `/candidates/$id` to open the detail view + promote to the
 * canonical catalog.
 *
 * Reviewers can also multi-select rows and bulk-promote them: the selected ids
 * are sent in one request to `POST /api/vendor-candidates/promote`, and each is
 * curated from its OWN stored data (no per-item editing). Matches auto-link to
 * the existing company (VI-549); a not-found or nameless candidate comes back in
 * the result's `failed` bucket and is reported in the summary.
 *
 * With `orphanOnly` (VI-765) the same grid is reused at `/orphaned` to list
 * ORPHAN records — non-curated candidates with zero linked tenants
 * (`tenant_count = 0`), left behind after a reconcile remap. Same columns and
 * bulk actions; only the server filter (`?orphan=true`), heading, and empty
 * copy differ.
 */
interface CandidateListViewProps {
    /** List only orphan records (non-curated, zero tenants). Default: the full candidate queue. */
    orphanOnly?: boolean;
}

export const CandidateListView = ({
    orphanOnly = false,
}: CandidateListViewProps = {}): React.JSX.Element => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchInput, 300);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [sortField, setSortField] = useState<CandidateSortField | null>(null);
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
    const [noTenantsOnly, setNoTenantsOnly] = useState(false);
    const [osintReportOnly, setOsintReportOnly] = useState(false);

    // Ids checked on the CURRENT page. Selection is cleared whenever the page or
    // search changes, so this set only ever holds ids that are visible right now.
    const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [bulkResult, setBulkResult] = useState<PromoteCandidatesResult | null>(null);
    // Snapshot of id → display name captured at promote time, so the result
    // summary can label failed rows even after the list refetches.
    const [promoteNames, setPromoteNames] = useState<Map<number, string>>(() => new Map());
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteResult, setDeleteResult] = useState<BulkDeleteCandidatesResult | null>(null);
    // Snapshot of id → display name captured at delete time, so the delete
    // summary can label failed rows even after the list refetches.
    const [deleteNames, setDeleteNames] = useState<Map<number, string>>(() => new Map());

    const offset = (page - 1) * pageSize;

    const { data, isLoading, error } = useQuery({
        queryKey: [
            'candidates',
            orphanOnly,
            debouncedSearch,
            page,
            pageSize,
            sortField,
            sortOrder,
            selectedProviders,
            noTenantsOnly,
            osintReportOnly,
        ],
        queryFn: () =>
            listCandidates({
                q: debouncedSearch || undefined,
                providers: selectedProviders.length > 0 ? selectedProviders : undefined,
                limit: pageSize,
                offset,
                sort: sortField ?? undefined,
                order: sortField ? sortOrder : undefined,
                // Non-curated + zero tenants is exactly the server's orphan filter, so
                // the "No tenants" toggle reuses it rather than adding a second param.
                orphan: orphanOnly || noTenantsOnly || undefined,
                hasOsintReport: osintReportOnly || undefined,
            }),
    });

    // Distinct providers present in the candidate queue — drives the filter's
    // checkbox options (only what's in the DB) + the "No provider" option.
    const { data: providerOptions } = useQuery({
        queryKey: ['candidate-providers'],
        queryFn: listCandidateProviders,
    });

    // Memoized so the reference is stable across renders (react-query returns a
    // stable `data`); `pageIds` below depends on it.
    const candidates = useMemo(() => data?.items ?? [], [data]);
    const total = data?.total ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    const clearSelection = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    // Reset selection + any prior summary when the visible page changes, so a
    // stale selection never leaks across pages or searches.
    const resetOnPageChange = useCallback(() => {
        clearSelection();
        setBulkResult(null);
        setDeleteResult(null);
    }, [clearSelection]);

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchInput(value);
            setPage(1);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    // Provider filter narrows the whole result set → reset to page 1 and clear
    // the page-scoped selection (same as search/sort).
    const handleProvidersChange = useCallback(
        (providers: string[]) => {
            setSelectedProviders(providers);
            setPage(1);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    // "No tenants" narrows the whole result set too → same reset as the provider filter.
    const handleNoTenantsChange = useCallback(
        (value: boolean) => {
            setNoTenantsOnly(value);
            setPage(1);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    // "Has OSINT report" narrows the whole result set too → same reset.
    const handleOsintReportChange = useCallback(
        (value: boolean) => {
            setOsintReportOnly(value);
            setPage(1);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    const handlePageChange = useCallback(
        (next: number) => {
            setPage(next);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    // Changing the page size resets to page 1 (the current offset is meaningless
    // under a different size) and clears the page-scoped selection.
    const handlePageSizeChange = useCallback(
        (value: string | null) => {
            if (!value) return;
            setPageSize(Number(value));
            setPage(1);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    const handleSelect = useCallback(
        (id: number) => {
            void navigate({ to: '/candidates/$id', params: { id } });
        },
        [navigate],
    );

    // Toggle direction when re-clicking the active column; otherwise switch
    // columns and start ascending. Reset to page 1 and clear the (page-scoped)
    // selection, since re-sorting changes which rows are visible on the page.
    const handleSort = useCallback(
        (field: CandidateSortField): void => {
            if (sortField === field) {
                setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            } else {
                setSortField(field);
                setSortOrder('asc');
            }
            setPage(1);
            resetOnPageChange();
        },
        [sortField, resetOnPageChange],
    );

    const toggleRow = useCallback((id: number, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    }, []);

    const pageIds = useMemo(() => candidates.map((c) => c.id), [candidates]);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
    const somePageSelected = pageIds.some((id) => selectedIds.has(id));

    const toggleAllOnPage = useCallback(
        (checked: boolean) => {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                for (const id of pageIds) {
                    if (checked) {
                        next.add(id);
                    } else {
                        next.delete(id);
                    }
                }
                return next;
            });
        },
        [pageIds],
    );

    const bulkMutation = useMutation({
        mutationFn: (ids: number[]) => promoteCandidates(ids),
        onSuccess: (result) => {
            setBulkResult(result);
            clearSelection();
            // Refresh the candidate list (curated rows flip / drop) AND the
            // Company Catalog list, whose rows + counts are created by promotion.
            void queryClient.invalidateQueries({ queryKey: ['candidates'] });
            void queryClient.invalidateQueries({ queryKey: ['vendors'] });
            notifications.show({
                color: result.failed.length > 0 ? 'red' : 'green',
                title: 'Bulk promote complete',
                message: summarizeResult(result),
            });
        },
        onError: (err: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Bulk promote failed',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (ids: number[]) => bulkDeleteCandidates(ids),
        onSuccess: (result) => {
            setDeleteResult(result);
            clearSelection();
            // Deleted candidates drop out of the review queue → refresh the list.
            void queryClient.invalidateQueries({ queryKey: ['candidates'] });
            notifications.show({
                color: result.failed.length > 0 ? 'red' : 'green',
                title: 'Bulk delete complete',
                message: summarizeDeleteResult(result),
            });
        },
        onError: (err: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Bulk delete failed',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    // Either bulk op in flight disables selection / row controls.
    const isPending = bulkMutation.isPending || deleteMutation.isPending;

    const handleConfirm = () => {
        setConfirmOpen(false);
        setBulkResult(null);
        const selected = candidates.filter((c) => selectedIds.has(c.id));
        setPromoteNames(new Map(selected.map((c) => [c.id, c.name ?? `#${String(c.id)}`])));
        bulkMutation.mutate(selected.map((c) => c.id));
    };

    const handleDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
        setDeleteResult(null);
        const selected = candidates.filter((c) => selectedIds.has(c.id));
        setDeleteNames(new Map(selected.map((c) => [c.id, c.name ?? `#${String(c.id)}`])));
        deleteMutation.mutate(selected.map((c) => c.id));
    };

    const selectedCount = selectedIds.size;

    return (
        <Stack
            gap="md"
            flex={1}
            mih={0}
            data-testid={orphanOnly ? 'OrphanedCandidateListView' : 'CandidateListView'}
        >
            <Box>
                <Title order={2}>{orphanOnly ? 'Orphan records' : 'Candidate Catalog'}</Title>
                {orphanOnly ? (
                    <Text size="sm" c="dimmed" mt={4}>
                        Non-curated candidates with zero linked tenants — left behind after a
                        reconcile remap.
                    </Text>
                ) : null}
            </Box>
            <Group gap="sm" align="flex-start" wrap="nowrap">
                <TextInput
                    flex={1}
                    placeholder="Search candidates by name or domain…"
                    leftSection={<IconSearch size={16} />}
                    value={searchInput}
                    onChange={(e) => {
                        handleSearchChange(e.currentTarget.value);
                    }}
                    data-testid="CandidateSearchInput"
                />
                <ProviderFilter
                    value={selectedProviders}
                    onChange={handleProvidersChange}
                    options={providerOptions?.providers ?? []}
                    hasNoProvider={providerOptions?.hasNoProvider ?? false}
                />
                {/* The Orphan records view is already tenant-less, so the toggle would be a no-op there. */}
                {orphanOnly ? null : (
                    <NoTenantsFilter value={noTenantsOnly} onChange={handleNoTenantsChange} />
                )}
                <OsintReportFilter value={osintReportOnly} onChange={handleOsintReportChange} />
            </Group>

            {bulkResult && !isPending ? (
                <BulkPromoteSummary
                    result={bulkResult}
                    names={promoteNames}
                    onDismiss={() => {
                        setBulkResult(null);
                    }}
                />
            ) : null}

            {deleteResult && !isPending ? (
                <BulkDeleteSummary
                    result={deleteResult}
                    names={deleteNames}
                    onDismiss={() => {
                        setDeleteResult(null);
                    }}
                />
            ) : null}

            {error ? (
                <Alert
                    icon={<IconAlertTriangle size={16} />}
                    color="red"
                    title="Failed to load candidates"
                    data-testid="CandidateListError"
                >
                    {error instanceof Error ? error.message : 'Unknown error'}
                </Alert>
            ) : isLoading ? (
                <Center flex={1}>
                    <Loader />
                </Center>
            ) : candidates.length === 0 ? (
                <Center flex={1}>
                    <Text c="dimmed" data-testid="CandidateListEmpty">
                        {orphanOnly ? 'No orphan records found.' : 'No candidates found.'}
                    </Text>
                </Center>
            ) : (
                <>
                    {selectedCount > 0 ? (
                        <Card withBorder radius="md" padding="sm" data-testid="BulkActionBar">
                            <Group justify="space-between" align="center">
                                <Text size="sm" fw={500}>
                                    {selectedCount} selected on this page
                                </Text>
                                <Group gap="sm">
                                    <Button
                                        variant="subtle"
                                        color="gray"
                                        size="sm"
                                        onClick={clearSelection}
                                        disabled={isPending}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        leftSection={<IconRocket size={16} />}
                                        size="sm"
                                        onClick={() => {
                                            setConfirmOpen(true);
                                        }}
                                        loading={bulkMutation.isPending}
                                        disabled={isPending}
                                        data-testid="BulkPromoteButton"
                                    >
                                        Promote selected ({selectedCount})
                                    </Button>
                                    <Button
                                        color="red"
                                        leftSection={<IconTrash size={16} />}
                                        size="sm"
                                        onClick={() => {
                                            setDeleteConfirmOpen(true);
                                        }}
                                        loading={deleteMutation.isPending}
                                        disabled={isPending}
                                        data-testid="CandidateBulkDeleteButton"
                                    >
                                        Delete selected ({selectedCount})
                                    </Button>
                                </Group>
                            </Group>
                        </Card>
                    ) : null}

                    <Table.ScrollContainer minWidth={760} maxHeight="100%" flex={1} mih={0}>
                        <Table
                            stickyHeader
                            highlightOnHover
                            striped
                            data-testid="CandidateListTable"
                        >
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th w={44}>
                                        <Checkbox
                                            aria-label="Select all candidates on this page"
                                            checked={allPageSelected}
                                            indeterminate={somePageSelected && !allPageSelected}
                                            onChange={(e) => {
                                                toggleAllOnPage(e.currentTarget.checked);
                                            }}
                                            disabled={isPending}
                                            data-testid="CandidateSelectAll"
                                        />
                                    </Table.Th>
                                    <SortableTh<CandidateSortField>
                                        label="ID"
                                        field="id"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                        width={70}
                                    />
                                    <SortableTh<CandidateSortField>
                                        label="Name"
                                        field="name"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <SortableTh<CandidateSortField>
                                        label="Provider"
                                        field="provider_abbreviation"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <SortableTh<CandidateSortField>
                                        label="Product"
                                        field="product_name"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <SortableTh<CandidateSortField>
                                        label="Domain"
                                        field="domain"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <Table.Th w={90}>Is Default</Table.Th>
                                    <SortableTh<CandidateSortField>
                                        label="Trust Center URL"
                                        field="trust_center_url"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <SortableTh<CandidateSortField>
                                        label="Tenants"
                                        field="tenant_count"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                        width={90}
                                    />
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {candidates.map((c) => (
                                    <CandidateRow
                                        key={c.id}
                                        candidate={c}
                                        selected={selectedIds.has(c.id)}
                                        onToggle={toggleRow}
                                        onSelect={handleSelect}
                                        disabled={isPending}
                                    />
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>

                    <Group justify="space-between" align="center">
                        <Group gap="sm" align="center">
                            <Text size="sm" c="dimmed">
                                Showing {offset + 1}–{Math.min(offset + candidates.length, total)}{' '}
                                of {total}
                            </Text>
                            <Select
                                aria-label="Rows per page"
                                data={PAGE_SIZE_OPTIONS}
                                value={String(pageSize)}
                                onChange={handlePageSizeChange}
                                allowDeselect={false}
                                w={90}
                                size="xs"
                                data-testid="CandidatePageSize"
                            />
                        </Group>
                        <Pagination value={page} onChange={handlePageChange} total={pageCount} />
                    </Group>
                </>
            )}

            <Modal
                opened={confirmOpen}
                onClose={() => {
                    setConfirmOpen(false);
                }}
                title="Promote selected candidates"
                centered
                data-testid="BulkPromoteConfirmModal"
            >
                <Stack gap="md">
                    <Text size="sm">
                        Promote {selectedCount} selected candidate{selectedCount === 1 ? '' : 's'}{' '}
                        using their existing data. Each becomes a curated catalog company + product.
                    </Text>
                    <Text size="sm" c="dimmed">
                        Candidates that already match an existing catalog entry (by name or domain),
                        or that have no name, are skipped and listed in the summary — nothing is
                        force-promoted.
                    </Text>
                    <Group justify="flex-end" gap="sm">
                        <Button
                            variant="default"
                            onClick={() => {
                                setConfirmOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            leftSection={<IconRocket size={16} />}
                            onClick={handleConfirm}
                            data-testid="BulkPromoteConfirmButton"
                        >
                            Promote {selectedCount}
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal
                opened={deleteConfirmOpen}
                onClose={() => {
                    setDeleteConfirmOpen(false);
                }}
                title="Delete selected candidates"
                centered
                data-testid="CandidateBulkDeleteConfirmModal"
            >
                <Stack gap="md">
                    <Text size="sm">
                        Delete {selectedCount} selected{' '}
                        {selectedCount === 1 ? 'candidate' : 'candidates'}? This removes them from
                        the review queue so they no longer appear in the Candidate Catalog.
                    </Text>
                    <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
                        This is a soft-delete — the candidate rows are marked deleted, not purged.
                        Only non-curated candidates can be removed here.
                    </Alert>
                    <Group justify="flex-end" gap="sm">
                        <Button
                            variant="default"
                            onClick={() => {
                                setDeleteConfirmOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            leftSection={<IconTrash size={16} />}
                            onClick={handleDeleteConfirm}
                            data-testid="CandidateBulkDeleteConfirmButton"
                        >
                            Delete {selectedCount}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};

/**
 * Human-readable one-liner for the completion toast.
 */
function summarizeResult(result: PromoteCandidatesResult): string {
    const parts = [`Promoted ${String(result.promoted.length)}`];
    if (result.failed.length > 0) {
        parts.push(`${String(result.failed.length)} failed`);
    }
    return `${parts.join(', ')}.`;
}

/**
 * Human-readable one-liner for the delete completion toast.
 */
function summarizeDeleteResult(result: BulkDeleteCandidatesResult): string {
    const parts = [
        `Deleted ${String(result.deleted.length)}`,
        `${String(result.skipped.length)} already removed`,
    ];
    if (result.failed.length > 0) {
        parts.push(`${String(result.failed.length)} failed`);
    }
    return `${parts.join(', ')}.`;
}

interface BulkPromoteSummaryProps {
    result: PromoteCandidatesResult;
    /** id → display name captured at promote time (the list may have refetched). */
    names: Map<number, string>;
    onDismiss: () => void;
}

/**
 * Inline panel shown after a bulk run: the promoted/failed tallies plus per-id
 * detail for anything that didn't promote (a not-found or nameless candidate
 * comes back as failed with the server's reason).
 */
const BulkPromoteSummary = ({
    result,
    names,
    onDismiss,
}: BulkPromoteSummaryProps): React.JSX.Element => {
    const { promoted, failed } = result;
    const label = (id: number): string => names.get(id) ?? `#${String(id)}`;

    return (
        <Card withBorder radius="md" padding="lg" data-testid="BulkPromoteSummary">
            <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                    <Box>
                        <Title order={4}>Bulk promote complete</Title>
                        <Text size="sm" c="dimmed" mt={4}>
                            Each candidate was curated from its own data; matches auto-linked to the
                            existing company.
                        </Text>
                    </Box>
                    <Button
                        variant="subtle"
                        size="xs"
                        onClick={onDismiss}
                        data-testid="BulkPromoteSummaryDismiss"
                    >
                        Dismiss
                    </Button>
                </Group>

                <Group gap="sm">
                    <Badge color="green" variant="light" data-testid="BulkSummaryPromoted">
                        Promoted {promoted.length}
                    </Badge>
                    <Badge color="red" variant="light" data-testid="BulkSummaryFailed">
                        Failed {failed.length}
                    </Badge>
                </Group>

                {failed.length > 0 ? (
                    <Box>
                        <Divider label="Failed" labelPosition="left" mb="xs" />
                        <List size="sm" spacing="xs">
                            {failed.map(({ id, error }) => (
                                <List.Item key={id}>
                                    <Text span fw={500}>
                                        {label(id)}
                                    </Text>{' '}
                                    <Text span c="dimmed">
                                        {error}
                                    </Text>
                                </List.Item>
                            ))}
                        </List>
                    </Box>
                ) : null}
            </Stack>
        </Card>
    );
};

interface BulkDeleteSummaryProps {
    result: BulkDeleteCandidatesResult;
    /** id → display name captured at delete time (the list may have refetched). */
    names: Map<number, string>;
    onDismiss: () => void;
}

/**
 * Inline panel shown after a bulk delete: the deleted/skipped/failed tallies
 * plus per-id detail for anything that didn't delete cleanly (an id already gone
 * comes back as skipped; any other error is listed as failed with the reason).
 */
const BulkDeleteSummary = ({
    result,
    names,
    onDismiss,
}: BulkDeleteSummaryProps): React.JSX.Element => {
    const { deleted, skipped, failed } = result;
    const label = (id: number): string => names.get(id) ?? `#${String(id)}`;

    return (
        <Card withBorder radius="md" padding="lg" data-testid="CandidateBulkDeleteSummary">
            <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                    <Box>
                        <Title order={4}>Bulk delete complete</Title>
                        <Text size="sm" c="dimmed" mt={4}>
                            Deleted candidates were soft-removed from the review queue.
                        </Text>
                    </Box>
                    <Button
                        variant="subtle"
                        size="xs"
                        onClick={onDismiss}
                        data-testid="CandidateBulkDeleteSummaryDismiss"
                    >
                        Dismiss
                    </Button>
                </Group>

                <Group gap="sm">
                    <Badge
                        color="green"
                        variant="light"
                        data-testid="CandidateDeleteSummaryDeleted"
                    >
                        Deleted {deleted.length}
                    </Badge>
                    <Badge color="gray" variant="light" data-testid="CandidateDeleteSummarySkipped">
                        Already removed {skipped.length}
                    </Badge>
                    <Badge color="red" variant="light" data-testid="CandidateDeleteSummaryFailed">
                        Failed {failed.length}
                    </Badge>
                </Group>

                {failed.length > 0 ? (
                    <Box>
                        <Divider label="Failed" labelPosition="left" mb="xs" />
                        <List size="sm" spacing="xs">
                            {failed.map(({ candidate_id, error }) => (
                                <List.Item key={candidate_id}>
                                    <Text span fw={500}>
                                        {label(candidate_id)}
                                    </Text>{' '}
                                    <Text span c="dimmed">
                                        {error}
                                    </Text>
                                </List.Item>
                            ))}
                        </List>
                    </Box>
                ) : null}
            </Stack>
        </Card>
    );
};

interface CandidateRowProps {
    candidate: CandidateEntry;
    selected: boolean;
    onToggle: (id: number, checked: boolean) => void;
    onSelect: (id: number) => void;
    disabled: boolean;
}

const CandidateRow = ({
    candidate,
    selected,
    onToggle,
    onSelect,
    disabled,
}: CandidateRowProps): React.JSX.Element => {
    // product_name + slug together disambiguate vendors with multiple products
    // (e.g. the 3 Google rows / 3 Microsoft rows the seed migration produces).
    const productLabel = candidate.product_name
        ? candidate.slug && candidate.slug !== 'default'
            ? `${candidate.product_name} (${candidate.slug})`
            : candidate.product_name
        : '—';
    const trustCenterHref = safeHttpUrl(candidate.trust_center_url);

    return (
        <Table.Tr
            style={{ cursor: 'pointer' }}
            onClick={() => onSelect(candidate.id)}
            data-testid="CandidateRow"
        >
            <Table.Td
                onClick={(e) => {
                    // Keep a checkbox click from bubbling to the row's navigate handler.
                    e.stopPropagation();
                }}
            >
                <Checkbox
                    aria-label={`Select ${candidate.name ?? `candidate ${String(candidate.id)}`}`}
                    checked={selected}
                    onChange={(e) => {
                        onToggle(candidate.id, e.currentTarget.checked);
                    }}
                    disabled={disabled}
                    data-testid="CandidateRowCheckbox"
                />
            </Table.Td>
            <Table.Td>
                <Text size="sm" c="dimmed">
                    {candidate.id}
                </Text>
            </Table.Td>
            <Table.Td>{candidate.name ?? '—'}</Table.Td>
            <Table.Td>
                <Text size="sm" c={candidate.provider_abbreviation ? undefined : 'dimmed'}>
                    {candidate.provider_abbreviation ?? '—'}
                </Text>
            </Table.Td>
            <Table.Td>
                <Text size="sm" c={candidate.product_name ? undefined : 'dimmed'}>
                    {productLabel}
                </Text>
            </Table.Td>
            <Table.Td>
                <Text size="sm" c="dimmed">
                    {candidate.domain ?? '—'}
                </Text>
            </Table.Td>
            <Table.Td>
                {candidate.is_default ? (
                    <Badge variant="light" color="blue" size="sm">
                        Default
                    </Badge>
                ) : (
                    <Text size="sm" c="dimmed">
                        —
                    </Text>
                )}
            </Table.Td>
            <Table.Td>
                {trustCenterHref ? (
                    <Anchor
                        href={trustCenterHref}
                        target="_blank"
                        rel="noreferrer"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {trustCenterHref}
                    </Anchor>
                ) : (
                    <Text size="sm" c="dimmed">
                        —
                    </Text>
                )}
            </Table.Td>
            <Table.Td>
                <Badge
                    variant={candidate.tenant_count ? 'light' : 'default'}
                    color={candidate.tenant_count ? 'teal' : 'gray'}
                >
                    {candidate.tenant_count}
                </Badge>
            </Table.Td>
        </Table.Tr>
    );
};
